import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Play, Pause, SkipForward } from 'lucide-react';
import { createAiPlayer, CROSSFADE_MS } from '../utils/aiMusicPlayer';
import { createBackgroundInterval } from '../utils/backgroundTimer';
import { useScreenWakeLock } from '../utils/useScreenWakeLock';
import { registerWakeHandler } from '../utils/wakeRecovery';
import { stopKeepAlive } from '../utils/keepAlive';
import { setSilentAudioPlaying } from '../utils/silentAudioAnchor';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const resolveSrc = (u) => (u ? (u.startsWith('http') ? u : `${BACKEND}${u}`) : '');
const RECENT_BUFFER = 20;
const PREFETCH_COUNT = 2;   // AI tracks held fully in memory ahead of the crossfade

// Fully download an AI track into memory and hand back an in-memory blob URL.
// Holding the actual bytes (not just warming the SW cache) is what lets the room
// keep playing clean through an extended wi-fi/4G outage — the next tracks are
// already local. Returns null on failure so callers fall back to the remote URL.
const downloadToBlob = async (url) => {
  if (!url) return null;
  try {
    const r = await fetch(url, { credentials: 'include' });
    if (!r.ok) return null;
    const blob = await r.blob();
    return URL.createObjectURL(blob);
  } catch { return null; }
};

const AiPlayerContext = createContext(null);
export const useAiPlayer = () => useContext(AiPlayerContext);

/**
 * App-wide owner of the AI crossfader. The `Audio` elements live here — above
 * the router — so in-house library playback keeps running when the venue
 * navigates away from the booth home to Settings, Guests, etc. Only AI-type
 * venues ever instantiate the player.
 */
export const AiPlayerProvider = ({ children }) => {
  const { user } = useAuth();
  const isAi = user?.client_type === 'ai';

  const [current, setCurrent] = useState(null);
  const [nextTrack, setNextTrack] = useState(null);
  const [progress, setProgress] = useState({ currentTime: 0, duration: 0, remainingMs: 0 });
  const [playing, setPlaying] = useState(false);
  const [settings, setSettings] = useState(null);
  const [taxonomy, setTaxonomy] = useState({ energies: [], genres: [] });

  const recentRef = useRef([]);
  const playerRef = useRef(null);
  const fetchingNextRef = useRef(false);
  const keepAliveOnRef = useRef(false);
  const currentRef = useRef(null);
  const playingRef = useRef(false);
  const nextTrackRef = useRef(null);
  const bufferRef = useRef([]);        // [{ track, buffered }] held fully in memory
  const fillingRef = useRef(false);
  const activeObjUrlRef = useRef(null); // blob URL of the track currently playing

  useEffect(() => { currentRef.current = current; }, [current]);
  useEffect(() => { playingRef.current = playing; }, [playing]);
  useEffect(() => { nextTrackRef.current = nextTrack; }, [nextTrack]);

  // Keep the tablet screen awake + the tab audible whenever playing so
  // Chrome doesn't throttle the engine and Android doesn't sleep the app.
  useScreenWakeLock(isAi && playing);

  // Ref-counted keep-alive tone + silent anchor: on while playing, off when
  // paused. Must first be armed inside a user gesture (handled in `arm()`).
  // NOTE (Jun 2026 background-audio hardening): the AI booth plays REAL
  // <audio> elements, which are themselves the OS media anchor and hold audio
  // focus. Unlike the commercial YouTube booth (which has no accessible audio
  // element and therefore needs the silent anchor), running a second silent
  // <audio> loop + keep-alive tone here COMPETES for — and can hijack — the
  // MediaSession away from the actual music, which is what caused the AI booth
  // to hiccup/stall when backgrounded on Android. So we deliberately do NOT
  // run the silent anchor or keep-alive tone for AI playback. The real track +
  // the MediaSession wiring below are what keep audio alive when the tablet is
  // backgrounded or screen-locked.

  // Called from the first user gesture (Play/Apply/Skip) to unlock the audio
  // session — required by autoplay policy on Android/iOS.
  // Kept for call-site compatibility. The AI engine unlocks the audio session
  // by playing its REAL <audio> element inside the user gesture (play / skip /
  // apply), so no silent-anchor priming is needed here.
  const arm = useCallback(() => {}, []);

  const fetchNext = useCallback(async (extraExclude = []) => {
    if (fetchingNextRef.current) return null;
    fetchingNextRef.current = true;
    try {
      const exclude = [...recentRef.current.slice(-RECENT_BUFFER), ...extraExclude].join(',');
      const now = new Date();
      const r = await axios.get(`${API}/ai-music/next`, {
        params: {
          exclude,
          local_hour: now.getHours(),
          local_minute: now.getMinutes(),
          local_dow: now.getDay(),   // 0 = Sunday … 6 = Saturday
        },
      });
      return { ...r.data, url: resolveSrc(r.data.url) };
    } catch {
      // Silent: callers use fetchNextResilient() which retries across network
      // blips, so a momentary drop at the track boundary never means dead air.
      return null;
    } finally {
      fetchingNextRef.current = false;
    }
  }, []);

  // Resilient next-track fetch — retries with backoff so a slow or dropped
  // connection at the crossfade boundary recovers instead of leaving the room
  // silent. ~8 attempts over ~25s covers the ~13s pre-fetch lead + overrun.
  const fetchNextResilient = useCallback(async (maxAttempts = 8, extraExclude = []) => {
    for (let i = 0; i < maxAttempts; i += 1) {
      const t = await fetchNext(extraExclude);
      if (t) return t;
      await new Promise((res) => setTimeout(res, Math.min(1500 + i * 500, 4000)));
    }
    return null;
  }, [fetchNext]);

  // Keep PREFETCH_COUNT upcoming tracks fully downloaded in memory. Each entry's
  // `url` is an in-memory blob URL (or the remote URL as a last-resort fallback),
  // so an extended outage never leaves the room in dead air.
  const fillBuffer = useCallback(async () => {
    if (fillingRef.current) return;
    fillingRef.current = true;
    try {
      while (bufferRef.current.length < PREFETCH_COUNT) {
        const bufIds = bufferRef.current.map((b) => b.track.id);
        const t = await fetchNextResilient(6, bufIds);
        if (!t) break;
        const objUrl = await downloadToBlob(t.url);
        bufferRef.current.push({ track: { ...t, url: objUrl || t.url }, buffered: !!objUrl });
      }
    } finally {
      fillingRef.current = false;
    }
  }, [fetchNextResilient]);

  // Drop the in-memory buffer (and free its blobs) when the vibe changes, so we
  // never crossfade into tracks fetched for the previous mood/energy/preset.
  const clearBuffer = useCallback(() => {
    bufferRef.current.forEach((e) => {
      if (e.track.url && e.track.url.startsWith('blob:')) {
        try { URL.revokeObjectURL(e.track.url); } catch { /* noop */ }
      }
    });
    bufferRef.current = [];
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([
        axios.get(`${API}/ai-library/taxonomy`),
        axios.get(`${API}/settings/ai-mode`, { params: { local_hour: new Date().getHours() } }),
      ]);
      setTaxonomy(t.data);
      setSettings(s.data);
    } catch { /* silent */ }
  }, []);

  // Instantiate the crossfader ONCE per session. It is intentionally never
  // destroyed on unmount — that's what makes playback survive route changes.
  useEffect(() => {
    if (!isAi || playerRef.current) return;
    const p = createAiPlayer({
      onTrackStart: (t) => {
        // Release the previous in-memory blob now that it's fully behind us.
        if (activeObjUrlRef.current && activeObjUrlRef.current !== t.url) {
          try { URL.revokeObjectURL(activeObjUrlRef.current); } catch { /* noop */ }
        }
        activeObjUrlRef.current = (t.url && t.url.startsWith('blob:')) ? t.url : null;
        setCurrent(t);
        setNextTrack(null);
        recentRef.current = [...recentRef.current.slice(-(RECENT_BUFFER - 1)), t.id];
        setPlaying(true);
        fillBuffer();                    // top the in-memory buffer back up to PREFETCH_COUNT
      },
      onNeedNextTrack: async () => {
        // Prefer a track already fully downloaded in memory so the crossfade
        // lands instantly and survives a live network drop; fall back to a
        // resilient remote fetch only if the buffer somehow ran dry. Wrapped
        // so a transient failure (e.g. a 401 at the track boundary) throws
        // nothing into the engine — the engine's end-of-track backstop keeps
        // re-asking on a throttle until a track arrives, so the room never
        // dies silently (RCA: Oct-2026 2-hour stall).
        try {
          const entry = bufferRef.current.shift();
          const t = entry ? entry.track : await fetchNextResilient();
          if (t && playerRef.current) {
            playerRef.current.queueNext(t);
            setNextTrack(t);
          }
        } catch { /* swallow — the engine backstop will retry */ }
        fillBuffer();
      },
      onProgress: (p2) => {
        setProgress(p2);
        // Keep the OS media-session position accurate. A live, updated session
        // signals Android/iOS that this is an active media app, making the OS
        // far less likely to reclaim/suspend the audio while backgrounded.
        if (typeof navigator !== 'undefined'
            && 'mediaSession' in navigator
            && navigator.mediaSession.setPositionState
            && p2.duration > 0) {
          try {
            navigator.mediaSession.setPositionState({
              duration: p2.duration,
              position: Math.min(p2.currentTime || 0, p2.duration),
              playbackRate: 1,
            });
          } catch { /* noop */ }
        }
      },
      onError: (e) => toast.error(`Playback error: ${e?.message || e}`),
    });
    p.setVolume(0.8);
    playerRef.current = p;
    loadAll();
  }, [isAi, fetchNext, fetchNextResilient, fillBuffer, loadAll]);

  // Reconnect safety net: if the network dropped right at the crossfade
  // boundary the next-track fetch may have failed; the moment we're back
  // online, grab one immediately so the engine always has something queued.
  useEffect(() => {
    if (!isAi || typeof window === 'undefined') return undefined;
    const onOnline = async () => {
      if (!playerRef.current || !playingRef.current) return;
      fillBuffer();                       // refill the in-memory buffer the instant we're back
      if (nextTrackRef.current) return;
      const entry = bufferRef.current.shift();
      const t = entry ? entry.track : await fetchNextResilient(4);
      if (t && playerRef.current && !nextTrackRef.current) {
        playerRef.current.queueNext(t);
        setNextTrack(t);
      }
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [isAi, fetchNextResilient, fillBuffer]);

  // Tear down the (otherwise never-destroyed) engine the moment the AI session
  // ends — logout or switching to a non-AI account. Without this the crossfader
  // keeps streaming audio in the background after sign-out.
  useEffect(() => {
    if (isAi || !playerRef.current) return;
    try { playerRef.current.destroy(); } catch { /* noop */ }
    playerRef.current = null;
    clearBuffer();
    if (activeObjUrlRef.current) {
      try { URL.revokeObjectURL(activeObjUrlRef.current); } catch { /* noop */ }
      activeObjUrlRef.current = null;
    }
    keepAliveOnRef.current = false;
    setSilentAudioPlaying(false);
    stopKeepAlive();
    recentRef.current = [];
    setPlaying(false);
    setCurrent(null);
    setNextTrack(null);
    setProgress({ currentTime: 0, duration: 0, remainingMs: 0 });
    if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
      try {
        navigator.mediaSession.metadata = null;
        navigator.mediaSession.playbackState = 'none';
      } catch { /* noop */ }
    }
  }, [isAi, clearBuffer]);

  const actionsRef = useRef({});

  // Lock-screen / OS media controls. Registering a MediaSession also signals
  // the OS this is a real media app, which helps audio survive screen lock.
  useEffect(() => {
    if (!isAi || typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    try {
      ms.setActionHandler('play', () => actionsRef.current.play?.());
      ms.setActionHandler('pause', () => actionsRef.current.pause?.());
      ms.setActionHandler('nexttrack', () => actionsRef.current.skip?.());
      ms.setActionHandler('previoustrack', null);
    } catch { /* some handlers unsupported */ }
  }, [isAi]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    if (!current) return;
    try {
      const artUrl = (typeof window !== 'undefined' ? window.location.origin : '') + '/media-art/mbai-cover.jpg';
      // eslint-disable-next-line no-undef
      navigator.mediaSession.metadata = new MediaMetadata({
        title: current.title || 'AI Track',
        artist: current.artist || 'MusicBooth AI',
        album: (current.genres && current.genres[0]) ? current.genres[0].replace(/-/g, ' ') : 'MusicBooth AI',
        // Branded neon cover so Bluetooth/cast screens (Google Home, car HUDs)
        // show artwork instead of a blank gradient. Several sizes point at the
        // same high-res image; the receiver scales to fit.
        artwork: [
          { src: artUrl, sizes: '256x256', type: 'image/jpeg' },
          { src: artUrl, sizes: '512x512', type: 'image/jpeg' },
          { src: artUrl, sizes: '1024x1024', type: 'image/jpeg' },
        ],
      });
      navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
    } catch { /* noop */ }
  }, [current, playing]);

  // Long-idle wake recovery: after Android backgrounds the tab for minutes,
  // resume the engine if it got suspended while we intended to keep playing.
  useEffect(() => {
    if (!isAi) return undefined;
    return registerWakeHandler(() => {
      if (playingRef.current && currentRef.current && playerRef.current?.isPaused()) {
        playerRef.current.resume();
      }
    });
  }, [isAi]);

  // TV now-playing push for AI venues — mirrors the commercial MusicContext
  // heartbeat so the themed TV renderer can show the live AI track as an
  // "ai:true" signature-sound slide (mood/cover generated by the theme).
  useEffect(() => {
    if (!isAi) return undefined;
    const send = () => {
      const t = currentRef.current;
      if (!t) return;
      const mood = (t.genres && t.genres[0]) ? t.genres[0].replace(/-/g, ' ') : (t.energy || 'signature');
      axios.post(`${API}/playback/heartbeat`, {
        is_playing: playingRef.current === true,
        now_playing: { ai: true, mood, title: null },
      }).catch(() => {});
    };
    send();
    const id = setInterval(send, 5000);
    return () => clearInterval(id);
  }, [isAi]);


  // AdminLayout, but AI booths run on plain Layout, so without this they never
  // showed up in the superadmin Analytics (active clients / hours online).
  // Pings every 30s with the live-playback flag so "Hours online" reflects
  // real audible time, exactly like commercial venues.
  useEffect(() => {
    if (!isAi) return undefined;
    const ping = () => {
      axios.post(`${API}/analytics/heartbeat`, { is_playing: playingRef.current === true }, { withCredentials: true }).catch(() => {});
    };
    ping();
    // Web-Worker interval so the hours heartbeat keeps firing even when the
    // tab is backgrounded / tablet screen is off. AI audio plays in the
    // background (hardened), so usage MUST be counted there too — a plain
    // setInterval is skipped/throttled while hidden and under-counts hours.
    const handle = createBackgroundInterval(ping, 30000);
    const onVisible = () => { if (document.visibilityState === 'visible') ping(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      handle.clear();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAi]);

  const play = useCallback(async () => {
    if (!playerRef.current) return;
    arm();
    if (current) {
      playerRef.current.resume();
      setPlaying(true);
      return;
    }
    const t = await fetchNext();
    if (t) playerRef.current.playNow(t);
  }, [current, fetchNext, arm]);

  const pause = useCallback(() => {
    playerRef.current?.pause();
    setPlaying(false);
  }, []);

  const skip = useCallback(async () => {
    arm();
    const entry = bufferRef.current.shift();
    const t = entry ? entry.track : await fetchNext();
    if (t && playerRef.current) playerRef.current.playNow(t);
    fillBuffer();
  }, [fetchNext, fillBuffer, arm]);

  const setVolume = useCallback((v) => playerRef.current?.setVolume(v), []);

  useEffect(() => {
    actionsRef.current = { play, pause, skip };
  }, [play, pause, skip]);

  const setMode = useCallback(async (mode) => {
    setSettings((s) => (s ? { ...s, mode } : s));
    try {
      const r = await axios.put(`${API}/settings/ai-mode`, { mode });
      setSettings((s) => ({ ...s, ...r.data }));
      clearBuffer();                      // mode switch changes which tracks get picked
      fillBuffer();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Mode switch failed');
      loadAll();
    }
  }, [loadAll, fillBuffer, clearBuffer]);

  const applyMood = useCallback(async (energy, genre) => {
    arm();
    const r = await axios.put(`${API}/settings/ai-mode`, {
      mode: 'manual',
      manual_energy: energy,
      manual_genres: [genre],
    });
    setSettings((s) => ({ ...s, ...r.data }));
    toast.success('Mood applied — new tracks will crossfade in.');
    clearBuffer();                        // old-mood buffer is now stale
    const t = await fetchNext();
    if (t && playerRef.current) playerRef.current.playNow(t);
    fillBuffer();
  }, [fetchNext, fillBuffer, clearBuffer, arm]);

  // One-tap swap to a saved base-vibe preset. Restores the preset's mode +
  // base genre/energy on the backend (which also restarts the rotation), then
  // crossfades straight into a track from the new vibe — no Settings trip.
  const applyPreset = useCallback(async (presetId) => {
    arm();
    const r = await axios.post(`${API}/settings/ai-mode/presets/${presetId}/apply`);
    setSettings((s) => ({ ...s, ...r.data }));
    clearBuffer();                        // previous vibe's buffer is now stale
    const t = await fetchNext();
    if (t && playerRef.current) playerRef.current.playNow(t);
    fillBuffer();
    return r.data;
  }, [fetchNext, fillBuffer, clearBuffer, arm]);

  const value = {
    isAi, current, nextTrack, progress, playing, settings, taxonomy,
    play, pause, skip, setVolume, setMode, applyMood, applyPreset, loadAll, CROSSFADE_MS,
  };

  return (
    <AiPlayerContext.Provider value={value}>
      {children}
      <AiMiniPlayer />
    </AiPlayerContext.Provider>
  );
};

/**
 * Slim persistent transport shown on every screen EXCEPT the booth home,
 * so a venue always knows music is still playing and can control it without
 * navigating back.
 */
const AiMiniPlayer = () => {
  const { isAi, current, playing, progress, play, pause, skip } = useAiPlayer();
  const location = useLocation();

  if (!isAi || !current) return null;
  if (location.pathname === '/') return null; // full booth already visible

  const genre = (current.genres && current.genres[0]) || 'library';
  const remainingS = Math.max(0, (progress.duration || 0) - (progress.currentTime || 0));

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[min(92vw,520px)]"
      data-testid="ai-mini-player"
    >
      <div className="flex items-center gap-3 rounded-2xl bg-black/80 backdrop-blur-xl ring-1 ring-white/10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)] px-3 py-2.5">
        <button
          type="button"
          onClick={playing ? pause : play}
          data-testid="ai-mini-toggle"
          aria-label={playing ? 'Pause' : 'Play'}
          className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center bg-white text-black hover:scale-105 active:scale-95 transition-transform"
        >
          {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>
        <Link to="/" className="min-w-0 flex-1 group" data-testid="ai-mini-open-booth">
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: playing ? '#39FF14' : '#FF007F' }}>
            {playing ? 'Playing' : 'Paused'}
            <span className="text-gray-600 ml-2 normal-case tracking-normal font-normal">
              {genre.replace(/-/g, ' ')} · {current.energy}
            </span>
          </p>
          <p className="text-sm text-white truncate group-hover:text-white/80 transition-colors">
            {current.title}
            <span className="text-gray-500"> — {current.artist || 'unknown'}</span>
          </p>
        </Link>
        {progress.duration > 0 && (
          <span className="text-[11px] text-gray-500 font-mono flex-shrink-0 hidden sm:block">
            -{Math.floor(remainingS / 60)}:{String(Math.floor(remainingS % 60)).padStart(2, '0')}
          </span>
        )}
        <button
          type="button"
          onClick={skip}
          data-testid="ai-mini-skip"
          aria-label="Skip"
          className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
        >
          <SkipForward className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
